/**
 * Mi Boleta — Checkout: selector de cuotas (wireflow 1.7 · Pago).
 *
 * Componente **controlado**: el padre es dueño de la selección porque el
 * checkout necesita ese número para mandárselo a la pasarela. Acá solo vive
 * la presentación y el cálculo derivado.
 *
 * Se monta únicamente cuando el método de pago admite diferido
 * (`supportsInstallments`), decisión del padre.
 *
 * Estilos: CSS Module con los tokens del Product Design System v1.0
 * (Sora + Mi Blue). Ver la cabecera de `InstallmentSelector.module.css` para
 * por qué va aislado y no con las clases Tailwind de commerce.
 */
import {
  INSTALLMENT_LEGAL_NOTE,
  INSTALLMENT_OPTIONS,
  calculateInstallmentPlan,
  formatCop,
  formatInstallmentLabel,
  type InstallmentPlan,
} from '@/lib/checkout/installments';

import styles from './InstallmentSelector.module.css';

export interface InstallmentSelectorProps {
  /** Valor total de la orden, en centavos (igual que `Money.amount_cents`). */
  totalCents: number;
  /** Cuotas seleccionadas (controlado). */
  value: number;
  onChange: (installments: number) => void;
  /** Plazos ofrecidos. Por defecto `INSTALLMENT_OPTIONS`. */
  options?: readonly number[];
  className?: string;
}

function InstallmentBreakdown({ plan }: { plan: InstallmentPlan }) {
  // Se compara lo FORMATEADO, no los centavos: el COP se muestra sin decimales,
  // así que un residuo de pocos centavos redondea al mismo peso y la línea
  // quedaría diciendo "Última cuota: $12.833" debajo de un "$12.833". Solo
  // avisamos cuando el fan de verdad vería un número distinto.
  const perLabel = formatCop(plan.perInstallmentCents);
  const lastLabel = formatCop(plan.lastInstallmentCents);
  const hasUnevenLast = lastLabel !== perLabel;

  return (
    <div className={styles.breakdown}>
      <div className={styles.row}>
        <span className={styles.rowLabel}>Valor total de la orden</span>
        <span className={styles.rowValue}>{formatCop(plan.totalCents)}</span>
      </div>

      <div className={styles.hero}>
        <span className={styles.heroLabel}>Cuota mensual aproximada</span>
        <div className={styles.heroRow}>
          <span className={styles.heroValue}>{perLabel}</span>
          <span className={styles.heroMultiplier}>{`× ${plan.installments}`}</span>
        </div>
        {hasUnevenLast && <span className={styles.unevenLast}>{`Última cuota: ${lastLabel}`}</span>}
      </div>

      <div className={styles.divider} />

      <p className={styles.legal}>{INSTALLMENT_LEGAL_NOTE}</p>
    </div>
  );
}

export function InstallmentSelector({
  totalCents,
  value,
  onChange,
  options = INSTALLMENT_OPTIONS,
  className,
}: InstallmentSelectorProps) {
  const plan = calculateInstallmentPlan(totalCents, value);

  return (
    <section className={className ? `${styles.root} ${className}` : styles.root}>
      <header className={styles.header}>
        <h3 className={styles.title}>Cuotas</h3>
        <p className={styles.subtitle}>Difiere tu compra con tarjeta de crédito</p>
      </header>

      <div className={styles.chipRow} role="radiogroup" aria-label="Número de cuotas">
        {options.map((count) => {
          const selected = count === value;
          return (
            <button
              key={count}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(count)}
              className={selected ? `${styles.chip} ${styles.chipSelected}` : styles.chip}
            >
              {formatInstallmentLabel(count)}
            </button>
          );
        })}
      </div>

      {plan.isDeferred ? (
        <InstallmentBreakdown plan={plan} />
      ) : (
        <p className={styles.single}>{`Pago en una sola cuota · ${formatCop(plan.totalCents)}`}</p>
      )}
    </section>
  );
}
